import {clsx} from 'clsx'
import {useState, useCallback, useLayoutEffect, useRef} from 'react'
import {gsap} from 'gsap'
import {usePrefersReducedMotion} from '@github-ui/use-prefers-reduced-motion'

import {MonaFooterGrid} from './MonaFooterGrid'
import {createMonaFooterOpeningAnimation} from './mona-footer-opening-animation'
import styles from './MonaFooter.module.css'
import {MonaCanvas, type MonaCanvasBounds, type MonaCanvasFaceDirection} from '../MonaCanvas'
import type {MonaFooterProps} from './types'
import blackFallback from './assets/fallback-monafooter-black.webp'
import purpleFallback from './assets/fallback-monafooter-purple.webp'
import {getMonaFooterLayoutSnapshot, useMonaFooterLayout} from './use-mona-footer-layout'

export type {FeaturedRepository, MonaFooterProps} from './types'

function createResponsiveOpeningAnimation() {
  return createMonaFooterOpeningAnimation(getMonaFooterLayoutSnapshot())
}

export function MonaFooter({className, mona, repos}: MonaFooterProps) {
  const layout = useMonaFooterLayout()
  const modelScale = {
    desktop: 1,
    tablet: 2,
    mobile: 4,
  }[layout]
  const prefersReducedMotion = usePrefersReducedMotion()
  const [isCanvasUnavailable, setIsCanvasUnavailable] = useState(false)
  const [hasStartedOpening, setHasStartedOpening] = useState(false)
  const [hasCompletedOpening, setHasCompletedOpening] = useState(false)
  const [skipOpeningAnimation, setSkipOpeningAnimation] = useState(false)
  const canvasPositionRef = useRef<HTMLDivElement>(null)
  const restingFaceDirectionRef = useRef<MonaCanvasFaceDirection | null>(null)
  const showFallback = isCanvasUnavailable || prefersReducedMotion

  const handleFallback = useCallback(() => setIsCanvasUnavailable(true), [])
  const handleOpeningStart = useCallback(() => {
    canvasPositionRef.current?.style.removeProperty('visibility')
    setHasStartedOpening(true)
  }, [])
  const handleLayoutChange = useCallback(() => {
    setSkipOpeningAnimation(true)
  }, [])
  const handleOpeningComplete = useCallback(() => {
    // WebGL renders the reset pose before React can commit the behind-grid styles.
    canvasPositionRef.current?.style.setProperty('visibility', 'hidden')
    const restingYawRadians = -Math.PI / 12
    restingFaceDirectionRef.current = {x: Math.sin(restingYawRadians), y: 0, z: Math.cos(restingYawRadians)}
    setHasCompletedOpening(true)
  }, [])
  const handleBoundsChange = useCallback((bounds: MonaCanvasBounds | null) => {
    const position = canvasPositionRef.current
    if (!bounds || !position) return

    position.style.setProperty('--rest-offset-x', `${(1 - bounds.width / 2 - bounds.centerX) * 100}%`)
    position.style.setProperty('--rest-offset-y', `${-bounds.centerY * 100}%`)
    position.style.setProperty('--reveal-distance', `${bounds.height * 50}%`)
  }, [])

  useLayoutEffect(() => {
    const position = canvasPositionRef.current
    if (!hasCompletedOpening || isCanvasUnavailable || prefersReducedMotion || !position) return

    position.style.removeProperty('visibility')
    if (skipOpeningAnimation) {
      position.style.setProperty('--reveal-progress', '0')
      return () => position.style.removeProperty('--reveal-progress')
    }

    const context = gsap.context(() => {
      gsap.to(position, {
        '--reveal-progress': 0,
        duration: 1,
        ease: 'expo.out',
      })
    }, position)
    return () => context.revert()
  }, [hasCompletedOpening, isCanvasUnavailable, prefersReducedMotion, skipOpeningAnimation])

  return (
    <div
      className={clsx(styles.MonaFooter, (hasCompletedOpening || showFallback) && styles.openingComplete, className)}
    >
      <div className={styles.Grid}>
        <MonaFooterGrid
          animate={hasStartedOpening || isCanvasUnavailable}
          showFinalState={skipOpeningAnimation}
          repos={repos}
          onLayoutChange={handleLayoutChange}
        />
      </div>
      {showFallback ? (
        <div className={styles.CanvasLayer}>
          <img
            alt={mona.alt ?? ''}
            className={styles.Fallback}
            height={mona.variant === 'dark' ? 391 : 415}
            src={mona.variant === 'dark' ? blackFallback : purpleFallback}
            width={mona.variant === 'dark' ? 292 : 314}
          />
        </div>
      ) : (
        <div
          className={styles.CanvasLayer}
          role={mona.alt ? 'img' : undefined}
          aria-label={mona.alt || undefined}
          aria-hidden={!mona.alt}
        >
          <div className={styles.CanvasPosition} ref={canvasPositionRef}>
            <MonaCanvas
              colorVariant={mona.variant === 'dark' ? 'black' : 'purple'}
              createOpeningAnimation={createResponsiveOpeningAnimation}
              modelScale={modelScale}
              skipOpeningAnimation={skipOpeningAnimation}
              faceDirectionRef={restingFaceDirectionRef}
              onFallback={handleFallback}
              onOpeningStart={handleOpeningStart}
              onOpeningComplete={handleOpeningComplete}
              onMonaCanvasBoundsChange={handleBoundsChange}
            />
          </div>
        </div>
      )}
    </div>
  )
}
